import { API_BASE } from "../config/apiBase";

const TOKEN_KEY = "token";
const USER_ID_KEY = "user_id";
const TOKEN_EXPIRY_KEY = "token_expiry";
const ROLE_KEY = "role";

export const AUTH_STATE_CHANGED_EVENT = "authStateChanged";
export const TOKEN_EXPIRED_EVENT = "tokenExpired";
export const TOKEN_REFRESH_BUFFER_MS = 60 * 1000;

const DEFAULT_ACCESS_TOKEN_LIFETIME_MS = 60 * 60 * 1000;

let refreshPromise: Promise<string> | null = null;
let sessionExpiryWasAnnounced = false;

export type AuthData = {
  token: string | null;
  userId: string | null;
  expiryTime: string | null;
  role: string | null;
};

type RefreshResponse = {
  accessToken?: string;
  userId?: string | number;
  role?: string;
};

export class AuthSessionExpiredError extends Error {
  code = "AUTH_SESSION_EXPIRED";

  constructor(message = "The authentication session has expired") {
    super(message);
    this.name = "AuthSessionExpiredError";
  }
}

export const isAuthSessionExpiredError = (error: unknown): boolean =>
  error instanceof AuthSessionExpiredError ||
  (typeof error === "object" &&
    error !== null &&
    "code" in error &&
    (error as { code?: string }).code === "AUTH_SESSION_EXPIRED");

const dispatchWindowEvent = (eventName: string): void => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(eventName));
  }
};

const decodeTokenExpiry = (token: string): number | null => {
  try {
    const payload = token.split(".")[1];
    if (!payload || typeof window === "undefined") return null;

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      Math.ceil(normalizedPayload.length / 4) * 4,
      "="
    );
    const decoded = JSON.parse(window.atob(paddedPayload)) as { exp?: number };

    return typeof decoded.exp === "number" ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

const readAuthData = (): AuthData => ({
  token: localStorage.getItem(TOKEN_KEY),
  userId: localStorage.getItem(USER_ID_KEY),
  expiryTime: localStorage.getItem(TOKEN_EXPIRY_KEY),
  role: localStorage.getItem(ROLE_KEY),
});

export const authService = {
  getTokenExpiryTime: (token: string): number =>
    decodeTokenExpiry(token) ?? Date.now() + DEFAULT_ACCESS_TOKEN_LIFETIME_MS,

  saveAuthData: (
    token: string,
    userId: string,
    expiryTime?: number,
    role?: string
  ): void => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_ID_KEY, String(userId));
    localStorage.setItem(
      TOKEN_EXPIRY_KEY,
      String(expiryTime ?? authService.getTokenExpiryTime(token))
    );

    const currentRole = localStorage.getItem(ROLE_KEY);
    const roleToStore = role ?? currentRole ?? "user";
    localStorage.setItem(ROLE_KEY, roleToStore);
    sessionExpiryWasAnnounced = false;
    dispatchWindowEvent(AUTH_STATE_CHANGED_EVENT);
  },

  clearAuthData: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_ID_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    localStorage.removeItem(ROLE_KEY);
    dispatchWindowEvent(AUTH_STATE_CHANGED_EVENT);
  },

  getAuthData: (): AuthData => readAuthData(),

  isTokenExpired: (): boolean => {
    const { token, expiryTime } = readAuthData();
    if (!token || !expiryTime) return true;

    const parsedExpiry = Number(expiryTime);
    return (
      !Number.isFinite(parsedExpiry) ||
      parsedExpiry - Date.now() <= TOKEN_REFRESH_BUFFER_MS
    );
  },

  announceExpiredSession: (): void => {
    authService.clearAuthData();
    if (sessionExpiryWasAnnounced) return;

    sessionExpiryWasAnnounced = true;
    dispatchWindowEvent(TOKEN_EXPIRED_EVENT);
  },

  refreshToken: async (): Promise<string> => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        const response = await fetch(`${API_BASE}/refresh-token`, {
          method: "POST",
          credentials: "include",
        });

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            throw new AuthSessionExpiredError();
          }
          throw new Error(`Token refresh failed with status ${response.status}`);
        }

        const data = (await response.json()) as RefreshResponse;
        const currentAuth = readAuthData();
        const userId = data.userId ?? currentAuth.userId;

        // Older backend deployments return only accessToken. Keep supporting
        // that response while the backend and frontend are rolled out.
        if (!data.accessToken || !userId) {
          throw new AuthSessionExpiredError("Invalid token refresh response");
        }

        authService.saveAuthData(
          data.accessToken,
          String(userId),
          authService.getTokenExpiryTime(data.accessToken),
          data.role ?? currentAuth.role ?? undefined
        );

        return data.accessToken;
      } catch (error) {
        if (isAuthSessionExpiredError(error)) {
          authService.announceExpiredSession();
        }
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  },

  handleUnauthorized: async (): Promise<string> => authService.refreshToken(),
};
